import Link from 'next/link';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SectionHeader } from '@components/SectionHeader';
import 'swiper/css';
import 'swiper/css/scrollbar';
import styles from './ImageGalleryBlock.styles';
import SpacingBlock from '@features/cms/components/collections/SpacingBlock/SpacingBlock';
import theme from '@styles/theme';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
export interface GalleryImage {
  id: string;
  src: string;
  alt?: string;
  href?: string;
}
export interface ImageGalleryBlockData {
  title?: string;
  titleLink?: string;
  images: GalleryImage[];
  backgroundColor?: string;
  marginTop?: number;
  marginBottom?: number;
  background?: string;
  code?: string;
}
export interface ImageGalleryBlockProps {
  data: ImageGalleryBlockData;
}
const ImageGalleryBlock = ({
  data
}: ImageGalleryBlockProps) => {
  const {
    title,
    titleLink,
    images,
    backgroundColor,
    marginTop,
    marginBottom,
    background,
    code
  } = data;
  const {
    t
  } = useTranslation('common');
  const sectionTitleAlt = useMemo(() => title ? title.replace(/<[^>]*>/g, '').trim() : '', [title]);
  const spacingBg = background || backgroundColor || theme.rawColors.neutralWhite;
  const getImageAlt = (image: GalleryImage, index: number) => {
    if (image.alt?.trim()) return image.alt.trim();
    if (sectionTitleAlt) return `${sectionTitleAlt} ${index + 1}`;
    return t('accessibility.galleryPhoto', {
      index: index + 1
    });
  };
  return <>
      {Number(marginTop) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: spacingBg,
      height: String(Number(marginTop)),
      code: 'mt'
    }} />}
      <section sx={styles.section(background || backgroundColor)} data-testid={code}>
        {/* Header */}
        {title && <>
            <div sx={styles.headerWrapper} data-testid="ImageGalleryBlock-headerWrapper">
              <SectionHeader title={title} sx={styles.headerTitle} />
              {titleLink && <Link href={titleLink} sx={styles.headerArrow}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M23.3297 36.667L22.1528 35.4903L36.8102 20.8361L0.00305695 20.9036L1.52356e-06 19.2395L36.8175 19.172L22.1528 4.51037L23.3297 3.33366L40 20.0003L23.3297 36.667Z" fill="#373737" />
                  </svg>
                </Link>}
            </div>
            <SpacingBlock data={{
          type: 'spacingBlock',
          backgroundColor: background || backgroundColor || theme.rawColors.beige500,
          height: '40',
          code: 'spacing-1'
        }} />
          </>}

        {/* Image slider */}
        <div sx={styles.sliderWrapper} data-testid="ImageGalleryBlock-sliderWrapper">
          <Swiper modules={[Scrollbar]} slidesPerView="auto" spaceBetween={8} breakpoints={{
          480: {
            spaceBetween: 16
          },
          768: {
            spaceBetween: 24
          },
          1024: {
            spaceBetween: 24
          },
          1280: {
            spaceBetween: 24
          },
          1600: {
            spaceBetween: 24
          }
        }} scrollbar={{
          draggable: true
        }} style={{
          paddingLeft: 'var(--gallery-offset)',
          paddingRight: 'var(--gallery-offset)'
        }}>
            {images.map((image, index) => <SwiperSlide key={image.id}>
                {image.href ? <Link href={image.href} sx={styles.imageLink} data-testid={`ImageGalleryBlock-imageLink-${index}`}>
                    <ResponsiveImage image={image.src} alt={getImageAlt(image, index)} sx={styles.image} />
                  </Link> : <ResponsiveImage image={image.src} alt={getImageAlt(image, index)} sx={styles.image} />}
              </SwiperSlide>)}
          </Swiper>
        </div>
      </section>
      {Number(marginBottom) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: spacingBg,
      height: String(Number(marginBottom)),
      code: 'mb'
    }} />}
    </>;
};
export default ImageGalleryBlock;